import { ReactElement, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  Label,
  Link,
  Paragraph,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { SafetyRepPending } from '../../../api/hms';
import { decisionError, type Decision, itemKey, versionRoute } from '../safetyRepView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from '../SafetyRep.module.css';

type Props = {
  item: SafetyRepPending;
  onDecide: (decision: Decision, comment: string) => void;
  deciding: boolean;
  /** «alreadyHandled» when someone else was first, «ownVersion» when the user made it as HR */
  error: 'alreadyHandled' | 'ownVersion' | 'failed' | null;
};

/** A version of the HMS setup to approve or send back. Shown under the task in «Mine oppgaver», which has the title. */
export const VersionDecision = ({ item, onDecide, deciding, error }: Props): ReactElement => {
  const { t } = useTranslation();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [comment, setComment] = useState('');
  const [tried, setTried] = useState(false);
  const key = itemKey(item);
  const invalid = tried && decision !== null && decisionError(decision, confirmed, comment);

  const start = (next: Decision | null) => {
    setDecision(next);
    setTried(false);
  };

  const confirm = () => {
    if (!decision) return;
    if (decisionError(decision, confirmed, comment)) {
      setTried(true);
      return;
    }
    onDecide(decision, comment.trim());
  };

  return (
    <div className={classes.decisionPanel}>
      <Paragraph data-size="sm" className={classes.text}>
        {t(`safetyRep.signing.text.${item.subject}`)}
      </Paragraph>
      <Link asChild data-size="sm" className={layout.startAligned}>
        <RouterLink to={versionRoute(item.subject, item.id)}>{t('safetyRep.signing.seeChanges')}</RouterLink>
      </Link>
      {decision === null && (
        <div className={layout.actions} data-gap="sm">
          <Button variant="primary" data-size="sm" onClick={() => start('approve')}>
            {t('safetyRep.signing.approve')}
          </Button>
          <Button variant="secondary" data-size="sm" onClick={() => start('return')}>
            {t('safetyRep.signing.report')}
          </Button>
        </div>
      )}
      {decision === 'approve' && (
        <div className={classes.decision}>
          <Checkbox
            data-size="sm"
            id={`${key}-confirm`}
            label={t(`safetyRep.signing.confirm.${item.subject}`)}
            checked={confirmed}
            aria-invalid={invalid || undefined}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {invalid && <ValidationMessage data-size="sm">{t('safetyRep.signing.confirmError')}</ValidationMessage>}
          <div className={layout.actions} data-gap="sm">
            <Button variant="primary" data-size="sm" onClick={confirm} loading={deciding}>
              {t('safetyRep.signing.approve')}
            </Button>
            <Button variant="secondary" data-size="sm" onClick={() => start(null)}>
              {t('safetyRep.signing.cancel')}
            </Button>
          </div>
        </div>
      )}
      {decision === 'return' && (
        <div className={classes.decision}>
          <Field data-size="sm">
            <Label htmlFor={`${key}-comment`}>{t('safetyRep.signing.missing')}</Label>
            <Field.Description id={`${key}-comment-description`}>
              {t('safetyRep.signing.missingDescription')}
            </Field.Description>
            <Textarea
              id={`${key}-comment`}
              rows={3}
              aria-describedby={`${key}-comment-description`}
              aria-invalid={invalid || undefined}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            {invalid && <ValidationMessage>{t('safetyRep.signing.missingError')}</ValidationMessage>}
          </Field>
          <div className={layout.actions} data-gap="sm">
            <Button variant="primary" data-size="sm" onClick={confirm} loading={deciding}>
              {t('safetyRep.signing.sendBack')}
            </Button>
            <Button variant="secondary" data-size="sm" onClick={() => start(null)}>
              {t('safetyRep.signing.cancel')}
            </Button>
          </div>
        </div>
      )}
      {error && (
        <Alert data-color="danger">
          <Paragraph data-size="sm">
            {t(
              error === 'alreadyHandled'
                ? 'safetyRep.signing.alreadyHandled'
                : error === 'ownVersion'
                  ? 'safetyRep.signing.ownVersion'
                  : 'hms.errors.save',
            )}
          </Paragraph>
        </Alert>
      )}
    </div>
  );
};
